/**
 * Right-slide drawer for forms (Branches, Cashiers, Rewards, etc.).
 *
 * Uses a portal-less inline overlay — keeps the implementation tight and
 * avoids the SSR/portal headaches that come with Radix here.
 *
 * Closes on backdrop click or Escape.
 */
import { useEffect } from 'react';
import { X } from 'lucide-react';

export default function Drawer({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer,
  size = 'md',
  testID,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [open, onClose]);

  if (!open) return null;
  const widthCls = { sm: 'w-[420px]', md: 'w-[520px]', lg: 'w-[680px]' }[size];

  return (
    <div className="fixed inset-0 z-50" data-testid={testID}>
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-[fadeIn_120ms_ease-out]"
        onClick={onClose}
      />
      <aside
        className={`absolute right-0 top-0 h-full max-h-[100vh] [max-height:100dvh] ${widthCls} max-w-full bg-[#0E0E13] border-l border-white/10 shadow-2xl flex flex-col animate-[slideIn_180ms_ease-out]`}
      >
        <header className="flex-shrink-0 px-6 py-5 border-b border-white/8 flex items-start justify-between gap-4">
          <div className="min-w-0">
            {title ? (
              <h2 className="font-display text-xl font-semibold truncate">{title}</h2>
            ) : null}
            {subtitle ? (
              <p className="text-xs text-white/50 mt-1">{subtitle}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="drawer-close"
            className="w-9 h-9 grid place-items-center rounded-lg text-white/60 hover:text-white hover:bg-white/5 transition"
          >
            <X size={18} />
          </button>
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-4">{children}</div>
        {footer ? (
          <footer className="sticky bottom-0 z-20 flex-shrink-0 px-6 py-4 border-t border-white/8 bg-[#0E0E13] flex items-center justify-end gap-2">
            {footer}
          </footer>
        ) : null}
      </aside>
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn { from { transform: translateX(24px); opacity: 0 } to { transform: translateX(0); opacity: 1 } }
      `}</style>
    </div>
  );
}
